import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Save, RotateCcw, Mail, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  founder: "Founder", admin: "Admin", client_manager: "Client Manager",
  designer: "Designer", engineer: "Engineer", strategist: "Strategist",
  operations: "Operations", intern: "Intern", client: "Client",
};

// Feb 2026 — Notification Preferences Matrix. Founder-editable, per-role,
// per-action toggle between "In-App Only" and "Email + In-App". Every
// action ALWAYS creates an in-app notification — this only decides whether
// it ALSO sends a real email. Backed by GET/PUT /api/notification-settings.
export function NotificationPreferencesPanel() {
  const [actions, setActions] = useState([]);
  const [staged, setStaged] = useState({}); // { action_key: { role: bool } }
  const [initial, setInitial] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notification-settings");
      setActions(data.actions || []);
      const seed = {};
      for (const a of data.actions || []) seed[a.key] = { ...(a.roles || {}) };
      setStaged(seed);
      setInitial(JSON.parse(JSON.stringify(seed)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const inHouse = useMemo(() => actions.filter((a) => a.category === "in_house"), [actions]);
  const clientActions = useMemo(() => actions.filter((a) => a.category === "client"), [actions]);

  const isDirty = JSON.stringify(staged) !== JSON.stringify(initial);

  const toggle = (actionKey, role) => {
    setStaged((prev) => ({
      ...prev,
      [actionKey]: { ...(prev[actionKey] || {}), [role]: !(prev[actionKey] || {})[role] },
    }));
  };

  const bulkAction = (actionKey, roles, value) => {
    setStaged((prev) => ({
      ...prev,
      [actionKey]: roles.reduce((acc, r) => ({ ...acc, [r]: value }), { ...(prev[actionKey] || {}) }),
    }));
  };

  const discard = () => setStaged(JSON.parse(JSON.stringify(initial)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/notification-settings", { actions: staged });
      const seed = {};
      for (const a of data.actions || []) seed[a.key] = { ...(a.roles || {}) };
      setActions(data.actions || []);
      setStaged(seed);
      setInitial(JSON.parse(JSON.stringify(seed)));
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-500" data-testid="notification-prefs-loading">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading Notification Preferences…
      </div>
    );
  }

  return (
    <div data-testid="notification-preferences-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><MessageSquareText size={12} /> In-App Only</span>
          <span className="text-slate-300">vs.</span>
          <span className="flex items-center gap-1 text-[#F54800] font-medium"><Mail size={12} /> Email + In-App</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500" data-testid="notification-prefs-dirty">
            {isDirty ? "Unsaved changes" : "No unsaved changes"}
          </span>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={discard} disabled={!isDirty || saving} data-testid="notification-prefs-discard">
            <RotateCcw size={13} className="mr-1.5" />Discard
          </Button>
          <Button size="sm" className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={save} disabled={!isDirty || saving} data-testid="notification-prefs-save">
            {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      <ActionGrid
        title="In-House Notifications"
        subtitle="Employees — a checked box emails that role in real time; unchecked keeps it in-app only."
        actionsList={inHouse}
        roleOrder={["founder", "admin", "client_manager", "designer", "engineer", "strategist", "operations", "intern"]}
        staged={staged}
        onToggle={toggle}
        onBulk={bulkAction}
      />

      <div className="h-6" />

      <ActionGrid
        title="Client Notifications"
        subtitle="Studio-wide default applied to every client — mute an individual client instead via their Registry profile."
        actionsList={clientActions}
        roleOrder={["client"]}
        staged={staged}
        onToggle={toggle}
        onBulk={bulkAction}
      />
    </div>
  );
}

function ActionGrid({ title, subtitle, actionsList, roleOrder, staged, onToggle, onBulk }) {
  if (actionsList.length === 0) return null;
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid={`notif-grid-${title.toLowerCase().includes("client") ? "client" : "in-house"}`}>
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <div className="font-display font-bold text-sm text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500">{subtitle}</div>
      </div>
      <div className="overflow-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50/60">
            <tr>
              <th className="text-left px-3 py-2 min-w-[200px] border-r border-slate-200">Action</th>
              {roleOrder.map((r) => (
                <th key={r} className="px-2 py-2 text-center font-medium text-slate-700 whitespace-nowrap min-w-[90px]">
                  {ROLE_LABELS[r] || r}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {actionsList.map((a) => {
              const rolesVal = staged[a.key] || {};
              const allOn = roleOrder.every((r) => rolesVal[r]);
              return (
                <tr key={a.key} className="border-t border-slate-200 hover:bg-slate-50/60" data-testid={`notif-row-${a.key}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">
                    <div className="flex items-center gap-2">
                      {a.label}
                      {roleOrder.length > 1 && (
                        <button
                          type="button"
                          onClick={() => onBulk(a.key, roleOrder, !allOn)}
                          className="text-[10px] text-[#F54800] hover:underline"
                          data-testid={`notif-bulk-${a.key}`}
                        >
                          {allOn ? "clear all" : "email all"}
                        </button>
                      )}
                    </div>
                  </td>
                  {roleOrder.map((r) => (
                    <td key={r} className="px-2 py-2 text-center" data-testid={`notif-cell-${a.key}-${r}`}>
                      <Checkbox
                        checked={!!rolesVal[r]}
                        onCheckedChange={() => onToggle(a.key, r)}
                        aria-label={`${a.label}: ${r}`}
                        className="data-[state=checked]:bg-[#F54800] data-[state=checked]:border-[#F54800]"
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
